/** The warehouse list, or the state that stands in for it: loading, the empty
 *  case (which distinguishes "none yet" from "none match"), or the table. */
import { Building2, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { Warehouse } from '../../../types/shared';

const TH = 'px-6 py-3 text-left text-sm font-semibold text-stone-900 dark:text-stone-100';
const TD = 'px-6 py-4 text-sm text-stone-600 dark:text-stone-400';

export default function WarehouseTable({
  rows,
  total,
  loading,
  onDelete,
}: {
  rows: Warehouse[];
  /** Everything loaded, before the search filter — it is what tells an empty
   *  list apart from a search that matched nothing. */
  total: number;
  loading: boolean;
  onDelete: (id: string) => void;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center">
        <Loader2 size={20} className="text-moss-600 animate-spin" />
        <span className="ml-2 text-stone-600 dark:text-stone-400">Loading warehouses...</span>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-12 text-center">
        <Building2 size={32} className="text-stone-300 dark:text-stone-700 mx-auto mb-3" />
        <p className="text-stone-600 dark:text-stone-400">
          {total === 0 ? 'No warehouses yet' : 'No warehouses match your search'}
        </p>
      </div>
    );
  }
  return (
    <table className="w-full">
      <thead className="bg-stone-50 dark:bg-[#242d28] border-b">
        <tr>
          <th className={TH}>Name</th>
          <th className={TH}>Code</th>
          <th className={TH}>Description</th>
          <th className={`${TH} text-right`}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((warehouse) => (
          <tr
            key={warehouse.id}
            className="border-b hover:bg-stone-50 dark:hover:bg-[#242d28] transition"
          >
            <td className="px-6 py-4 text-sm font-medium text-bark-900 dark:text-stone-100">
              {warehouse.name}
            </td>
            <td className={TD}>{warehouse.code || '—'}</td>
            <td className={TD}>{warehouse.description || '—'}</td>
            <td className="px-6 py-4 text-sm text-right space-x-2">
              <button
                onClick={() => navigate(`/admin/settings/warehouses/${warehouse.id}`)}
                className="inline-flex items-center gap-1 px-3 py-1 text-moss-600 dark:text-moss-400 hover:bg-moss-50 dark:hover:bg-moss-900/20 rounded transition"
              >
                Edit
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => onDelete(warehouse.id)}
                className="inline-flex items-center gap-1 px-3 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
              >
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
